import { zodResolver } from '@hookform/resolvers/zod';
import { Action, Alert, Modal, TextAreaInput, useToast } from 'corso-admin-ui';
import { crewMerchantShippingClaimApi } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { useForm } from 'react-hook-form';
import { useCurrency } from '~/hooks/useCurrency';
import { isErrorMessage } from '~/utils/errorResponse';
import { useShippingClaimRefundShipping } from '../hooks/useShippingClaimManualRefund';
import { useShippingClaim } from '../ShippingClaimReviewProvider';

const refundShippingSchema =
  crewMerchantShippingClaimApi[
    '/:storeId/shipping-protection/claim/:shippingClaimId/refund-shipping'
  ].post.request.body;

export function RefundShippingShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { currencyCode } = useCurrency();
  const { originalStoreOrder } = useShippingClaim();
  const { mutate: refundShipping, isPending } =
    useShippingClaimRefundShipping();

  const formId = 'refund-shipping-shipping-claim-form';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(refundShippingSchema),
    defaultValues: { comment: '' },
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Refund Shipping"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Refund Shipping
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((data) =>
            refundShipping(data, {
              onSuccess: closeAndReset,
              onError: (error) =>
                toast.show(
                  isErrorMessage(error)
                    ? error.message
                    : 'Could not refund shipping. Please try again.',
                ),
            }),
          )(e).catch((error: unknown) =>
            console.error('Failed to refund shipping', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <Alert
          variant="tip"
          heading={`This refunds the order's shipping${
            originalStoreOrder.shippingPrice === undefined
              ? ''
              : ` (${formatter.currency(originalStoreOrder.shippingPrice, currencyCode)} left)`
          } on Shopify and resolves the claim as refunded.`}
        />
        <TextAreaInput
          id="refund-shipping-shipping-claim-comment"
          label="Comment"
          details="This comment will be added to the timeline."
          rows={3}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </form>
    </Modal>
  );
}
